/**
 * Base HTTP client for every backend call from the SPA.
 *
 * Responsibilities:
 *   - Attach session cookie (`credentials: 'include'`).
 *   - Base URL from `VITE_API_BASE` (empty string in dev/prod — same origin).
 *   - Throw a typed {@link ApiError} on non-2xx.
 *   - Redirect the browser to `/login` on 401 (handled exactly once).
 *
 * Feature code must never call `fetch` directly — always go through
 * `apiGet` / `apiPost` / `apiRequest` so the 401 interceptor stays central.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly payload: unknown;

  constructor(message: string, status: number, payload: unknown, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
    this.code = code;
  }
}

export interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  /** JSON body; serialized automatically when `method` is not GET/HEAD. */
  json?: unknown;
  /** Raw body (takes precedence over `json`). */
  body?: BodyInit | null;
  /** Override the 401 redirect behaviour (default: redirect). */
  redirectOn401?: boolean;
}

const API_BASE: string = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

/** Window-scoped guard so concurrent 401s don't stack browser navigations. */
let redirectInFlight = false;

/**
 * Custom event dispatched when an API request returns 401. The `AuthProvider`
 * subscribes and performs a client-side (React Router) redirect via
 * `useNavigate`. If no listener is registered (pre-Provider bootstrap),
 * we fall back to `window.location.assign` so the user still reaches /login.
 */
export const API_UNAUTHORIZED_EVENT = 'api:unauthorized';

export interface ApiUnauthorizedEventDetail {
  next: string;
}

function buildUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  if (!API_BASE) return path;
  if (path.startsWith('/')) return `${API_BASE}${path}`;
  return `${API_BASE}/${path}`;
}

function redirectToLogin(): void {
  if (redirectInFlight) return;
  redirectInFlight = true;
  const next = window.location.pathname + window.location.search;

  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    const event = new CustomEvent<ApiUnauthorizedEventDetail>(API_UNAUTHORIZED_EVENT, {
      detail: { next },
      cancelable: true,
    });
    const notCancelled = window.dispatchEvent(event);
    // If the AuthProvider handled it via preventDefault(), do not fall back.
    if (!notCancelled) return;
  }

  // Fallback for non-SPA contexts (tests, bootstrap race).
  const target = `/login?next=${encodeURIComponent(next)}`;
  if (typeof window !== 'undefined') {
    window.location.assign(target);
  }
}

async function parseBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }
  try {
    return await response.text();
  } catch {
    return null;
  }
}

export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const { json, body, redirectOn401 = true, headers, method, ...rest } = options;

  const finalHeaders = new Headers(headers);
  let finalBody: BodyInit | null | undefined = body ?? undefined;

  if (json !== undefined && finalBody === undefined) {
    finalBody = JSON.stringify(json);
    if (!finalHeaders.has('content-type')) {
      finalHeaders.set('content-type', 'application/json');
    }
  }
  if (!finalHeaders.has('accept')) {
    finalHeaders.set('accept', 'application/json');
  }

  const response = await fetch(buildUrl(path), {
    credentials: 'include',
    method: method ?? (finalBody ? 'POST' : 'GET'),
    headers: finalHeaders,
    body: finalBody,
    ...rest,
  });

  if (response.status === 401) {
    if (redirectOn401) {
      redirectToLogin();
    }
    const payload = await parseBody(response);
    throw new ApiError('Unauthorized', 401, payload, 'UNAUTHORIZED');
  }

  if (!response.ok) {
    const payload = await parseBody(response);
    const message =
      (payload && typeof payload === 'object' && 'message' in payload
        ? String((payload as { message: unknown }).message)
        : response.statusText) || `Request failed with status ${response.status}`;
    const code =
      payload && typeof payload === 'object' && 'code' in payload
        ? String((payload as { code: unknown }).code)
        : undefined;
    throw new ApiError(message, response.status, payload, code);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await parseBody(response)) as T;
}

export function apiGet<T>(path: string, options?: ApiRequestOptions): Promise<T> {
  return apiRequest<T>(path, { ...options, method: 'GET' });
}

/**
 * POST helper. `json` is serialized to the request body and the
 * `content-type: application/json` header is set automatically.
 */
export function apiPost<T>(
  path: string,
  json?: unknown,
  options?: ApiRequestOptions,
): Promise<T> {
  return apiRequest<T>(path, { ...options, method: 'POST', json });
}

/**
 * Reset the redirect guard. Exported for tests and for the AuthProvider
 * (which re-arms it after a successful logout->login cycle completes).
 */
export function resetUnauthorizedGuard(): void {
  redirectInFlight = false;
}

/** @deprecated kept for compatibility; use {@link resetUnauthorizedGuard}. */
export function __resetApiClientForTests(): void {
  resetUnauthorizedGuard();
}
